// Modal dodawania / edycji pozycji kalkulacji.
// Tryb DODAJ: 3 taby Produkt / Usluga / Pozycja wlasna, POST.
// Tryb EDYTUJ: prop `editPosition` ≠ null → formularz preloadowany, ukryte taby/wyszukiwarka, PUT.
// Wspolne pola: name, priceType, priceNet, taxPercent, priceGross (dwukierunkowe auto-przeliczanie net <-> gross).
// Pole `quantity` zostalo usuniete — finalna ilosc pochodzi z pozycji oferty (kalkulacja jest cena bazowa pozycji).

import React, { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tab,
  Tabs,
  TextField,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Formik, type FormikHelpers } from 'formik';
import * as yup from 'yup';

import { WiseModal } from 'components/wise';
import {
  axios,
  transformApiErrorsToFormik,
  useGetClientProducts,
  useGetServicesDictionary,
  usePostSalesCrmOfferCalculationPosition,
  usePutSalesCrmOfferCalculationPosition,
  type CalculationPositionPriceType,
  type ISalesCrmOfferCalculationPosition,
  type IServiceDictionaryItem,
  type ISalesCrmProductPurchaseCostResponse
} from 'api';
import type { ICommandResponseError } from 'api/types';
import type { IProductListItem } from 'api/types';

import styles from 'theme/components/containers/TraderClientOfferProductsContainer/AddCalculationPositionModal.module.scss';

type TabKey = 'product' | 'service' | 'custom';

interface IAddCalculationPositionModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  clientId: number;
  offerId: string;
  calculationId: number;
  /** Gdy ustawione → tryb edycji: formularz preloadowany, PUT zamiast POST, brak wyszukiwarek. */
  editPosition?: ISalesCrmOfferCalculationPosition | null;
}

interface IFormValues {
  name: string;
  productId: number | null;
  serviceId: number | null;
  priceType: CalculationPositionPriceType;
  priceNet: number;
  taxPercent: number;
  priceGross: number;
  /** Koszt własny netto — ile my placimy dostawcy. Opcjonalne. */
  costNet: number | null;
}

const DEFAULT_TAX_PERCENT = 23;
const DEBOUNCE_MS = 300;
const DICTIONARY_LIMIT = 20;
const MIN_SEARCH_LENGTH = 2;

const defaultValues = (): IFormValues => ({
  name: '',
  productId: null,
  serviceId: null,
  priceType: 'TOTAL',
  priceNet: 0,
  taxPercent: DEFAULT_TAX_PERCENT,
  priceGross: 0,
  costNet: null
});

const computeGross = (net: number, tax: number): number => {
  if (Number.isNaN(net) || Number.isNaN(tax)) return 0;
  return Math.round(net * (1 + tax / 100) * 100) / 100;
};

const computeNet = (gross: number, tax: number): number => {
  if (Number.isNaN(gross) || Number.isNaN(tax)) return 0;
  const divisor = 1 + tax / 100;
  if (divisor === 0) return 0;
  return Math.round((gross / divisor) * 100) / 100;
};

/**
 * Blokuje wpisywanie znakow ktore HTML <input type="number"> akceptuje, ale nie ma sensu
 * dla cen/ilosci: 'e'/'E' (wykladnik), '+', '-' (wartosci ujemne — pola maja min: 0).
 * Przepuszczamy cyfry, kropki, przecinki oraz klawisze nawigacyjne (Backspace, Tab, strzalki, Home/End, Delete).
 */
const blockNonNumericKeys = (event: React.KeyboardEvent<HTMLInputElement>): void => {
  if (['e', 'E', '+', '-'].includes(event.key)) {
    event.preventDefault();
  }
};

const productPriceNet = (item: IProductListItem): number | null => {
  const prices = item.product_price_short_info?.prices;
  if (!prices || prices.length === 0) return null;
  return prices[0].price_net ?? null;
};

const productPriceGross = (item: IProductListItem): number | null => {
  const prices = item.product_price_short_info?.prices;
  if (!prices || prices.length === 0) return null;
  return prices[0].price_gross ?? null;
};

const productName = (item: IProductListItem): string =>
  item.product_basic_short_info?.title ?? item.product_basic_full_info?.title ?? '';

const AddCalculationPositionModal: FC<IAddCalculationPositionModalProps> = ({
  open,
  onClose,
  onSaved,
  clientId,
  offerId,
  calculationId,
  editPosition = null
}) => {
  const { t } = useTranslation();

  const isEditMode = editPosition != null;

  const [activeTab, setActiveTab] = useState<TabKey>('product');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Search w slownikach (dla zakladek Produkt/Usluga).
  const [productSearchInput, setProductSearchInput] = useState<string>('');
  const [productSearchDebounced, setProductSearchDebounced] = useState<string>('');
  const [serviceSearchInput, setServiceSearchInput] = useState<string>('');
  const [serviceSearchDebounced, setServiceSearchDebounced] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    setActiveTab('product');
    setProductSearchInput('');
    setProductSearchDebounced('');
    setServiceSearchInput('');
    setServiceSearchDebounced('');
    setErrorMessage(null);
  }, [open]);

  // Debounce.
  useEffect(() => {
    const h = setTimeout(() => setProductSearchDebounced(productSearchInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [productSearchInput]);

  useEffect(() => {
    const h = setTimeout(() => setServiceSearchDebounced(serviceSearchInput.trim()), DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [serviceSearchInput]);

  const { data: productsData, isFetching: isFetchingProducts } = useGetClientProducts(
    clientId,
    productSearchDebounced,
    {
      enabled:
        open && activeTab === 'product' && productSearchDebounced.length >= MIN_SEARCH_LENGTH
    }
  );
  const productItems: IProductListItem[] = productsData?.items ?? [];

  const { data: servicesData, isFetching: isFetchingServices } = useGetServicesDictionary(
    serviceSearchDebounced,
    DICTIONARY_LIMIT,
    { enabled: open && activeTab === 'service' }
  );
  const serviceItems: IServiceDictionaryItem[] = servicesData?.items ?? [];

  // ====== Validation ======
  const validationSchema = useMemo(
    () =>
      yup.object({
        name: yup.string().trim().required(t('common.fieldRequired')),
        priceType: yup.string().oneOf(['TOTAL', 'UNIT']).required(),
        priceNet: yup
          .number()
          .typeError(t('salesCrm.calculation.addPosition.error.priceNetMustBeNumber'))
          .positive(t('salesCrm.calculation.addPosition.error.priceNetMustBePositive'))
          .required(),
        taxPercent: yup.number().min(0).nullable(),
        priceGross: yup.number().min(0).nullable()
      }),
    [t]
  );

  // ====== Mutations ======
  const { mutateAsync: addPositionAsync, isLoading: isAdding } =
    usePostSalesCrmOfferCalculationPosition(clientId, offerId, calculationId);
  const { mutateAsync: updatePositionAsync, isLoading: isUpdating } =
    usePutSalesCrmOfferCalculationPosition(clientId, offerId, calculationId);
  const isSaving = isAdding || isUpdating;

  const handleTabChange = (next: TabKey, setValues: (v: IFormValues) => void) => {
    setActiveTab(next);
    setErrorMessage(null);
    // Resetujemy formularz przy zmianie zakladki — wspolne pola wracaja do domyslnych,
    // a dane z poprzedniego slownika znikaja.
    setValues(defaultValues());
  };

  const handleSelectProduct = (
    item: IProductListItem,
    setValues: (v: IFormValues) => void,
    setFieldValue: (field: keyof IFormValues, value: unknown) => void,
    values: IFormValues
  ) => {
    const net = productPriceNet(item) ?? 0;
    const gross = productPriceGross(item);
    setValues({
      ...values,
      name: productName(item),
      productId: item.id,
      serviceId: null,
      priceNet: net,
      priceGross: gross ?? computeGross(net, values.taxPercent),
      // costNet bedzie prepopulowany asynchronicznie z cennika kosztu zakupu.
      costNet: null
      // taxPercent nie zwracany przez listing produktow — zostawiamy aktualny.
    });

    // Async fetch kosztu zakupu z cennika `purchase_cost_price_list_symbol`.
    // Najtansza cena z cennika sposrod jednostek produktu (backend wybiera min).
    // Brak ceny -> zostawiamy null, handlowiec moze wpisac recznie.
    axios
      .get<ISalesCrmProductPurchaseCostResponse>(
        `/sales-crm/clients/${clientId}/products/${item.id}/purchase-cost`
      )
      .then((response) => {
        const cost = (response as unknown as ISalesCrmProductPurchaseCostResponse)
          ?.purchaseCostNet;
        if (cost !== null && cost !== undefined) {
          setFieldValue('costNet', cost);
        }
      })
      .catch(() => {
        // Cichy fail — to tylko podpowiedz, handlowiec moze wpisac recznie.
      });
  };

  const handleSelectService = (
    item: IServiceDictionaryItem,
    setValues: (v: IFormValues) => void,
    values: IFormValues
  ) => {
    const tax = item.taxPercent ?? values.taxPercent;
    setValues({
      ...values,
      name: item.name,
      serviceId: item.id,
      productId: null,
      taxPercent: tax,
      priceGross: computeGross(values.priceNet, tax)
    });
  };

  const handleSubmit = async (
    values: IFormValues,
    helpers: FormikHelpers<IFormValues>
  ) => {
    setErrorMessage(null);
    const payload = {
      name: values.name.trim(),
      productId: values.productId,
      serviceId: values.serviceId,
      priceType: values.priceType,
      priceNet: values.priceNet,
      priceGross: values.priceGross,
      taxPercent: values.taxPercent,
      costNet: values.costNet
    };
    try {
      if (isEditMode && editPosition) {
        await updatePositionAsync({ positionId: editPosition.id, payload });
      } else {
        await addPositionAsync(payload);
      }
      onSaved();
    } catch (error) {
      const apiError = error as ICommandResponseError;
      if (apiError?.error_fields?.length) {
        const formikErrors = transformApiErrorsToFormik(apiError.error_fields);
        helpers.setErrors(formikErrors);
      } else {
        setErrorMessage(
          apiError?.message || t('salesCrm.calculation.addPosition.error.submitFailed')
        );
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const initialValues = useMemo<IFormValues>(() => {
    if (isEditMode && editPosition) {
      return {
        name: editPosition.name ?? '',
        productId: editPosition.productId ?? null,
        serviceId: editPosition.serviceId ?? null,
        priceType: editPosition.priceType,
        priceNet: editPosition.priceNet ?? 0,
        taxPercent: editPosition.taxPercent ?? DEFAULT_TAX_PERCENT,
        priceGross: editPosition.priceGross ?? 0,
        costNet: editPosition.costNet ?? null
      };
    }
    return defaultValues();
  }, [isEditMode, editPosition]);

  return (
    <WiseModal
      open={open}
      onClose={onClose}
      title={t(
        isEditMode
          ? 'salesCrm.calculation.editPosition.title'
          : 'salesCrm.calculation.addPosition.title'
      )}
      maxWidth={720}>
      <Formik<IFormValues>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize>
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          setValues,
          submitForm
        }) => {
          // Sekcja formularza (Nazwa + Typ ceny + ceny + ilosc) widoczna:
          // - zawsze w trybie edycji,
          // - zawsze w tabie "custom" (pozycja wlasna),
          // - w tabie "product" tylko po wybraniu produktu (productId != null),
          // - w tabie "service" tylko po wybraniu uslugi (serviceId != null).
          const showFormFields =
            isEditMode
            || activeTab === 'custom'
            || (activeTab === 'product' && values.productId != null)
            || (activeTab === 'service' && values.serviceId != null);

          return (
            <div className={styles.body}>
              {!isEditMode && (
                <Tabs
                  value={activeTab}
                  onChange={(_, value) => handleTabChange(value as TabKey, setValues)}
                  className={styles.tabs}
                  variant="fullWidth">
                  <Tab value="product" label={t('salesCrm.calculation.addPosition.tab.product')} />
                  <Tab value="service" label={t('salesCrm.calculation.addPosition.tab.service')} />
                  <Tab value="custom" label={t('salesCrm.calculation.addPosition.tab.custom')} />
                </Tabs>
              )}

              {!isEditMode && activeTab === 'product' && (
                <div className={styles.searchRow}>
                  <TextField
                    label={t('salesCrm.calculation.addPosition.search.productLabel')}
                    value={productSearchInput}
                    onChange={(e) => setProductSearchInput(e.target.value)}
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder={t('salesCrm.calculation.addPosition.search.productPlaceholder')}
                  />
                  <div className={styles.results}>
                    {isFetchingProducts && (
                      <div className={styles.loading}>
                        <CircularProgress size={20} />
                      </div>
                    )}
                    {!isFetchingProducts &&
                      productSearchDebounced.length < MIN_SEARCH_LENGTH && (
                        <div className={styles.empty}>
                          {t('salesCrm.calculation.addPosition.search.hint')}
                        </div>
                      )}
                    {!isFetchingProducts &&
                      productSearchDebounced.length >= MIN_SEARCH_LENGTH &&
                      productItems.length === 0 && (
                        <div className={styles.empty}>
                          {t('salesCrm.calculation.addPosition.search.noResults')}
                        </div>
                      )}
                    {!isFetchingProducts &&
                      productItems.map((item) => {
                        const isSelected = values.productId === item.id;
                        const net = productPriceNet(item);
                        return (
                          <button
                            type="button"
                            key={item.id}
                            className={`${styles.resultItem} ${isSelected ? styles.resultItemSelected : ''}`}
                            onClick={() => handleSelectProduct(item, setValues, setFieldValue, values)}>
                            <div className={styles.resultName}>{productName(item)}</div>
                            {net != null && (
                              <div className={styles.resultMeta}>
                                {t('salesCrm.calculation.addPosition.field.priceNet')}:{' '}
                                {net.toFixed(2)}
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {!isEditMode && activeTab === 'service' && (
                <div className={styles.searchRow}>
                  <TextField
                    label={t('salesCrm.calculation.addPosition.search.serviceLabel')}
                    value={serviceSearchInput}
                    onChange={(e) => setServiceSearchInput(e.target.value)}
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder={t('salesCrm.calculation.addPosition.search.servicePlaceholder')}
                  />
                  <div className={styles.results}>
                    {isFetchingServices && (
                      <div className={styles.loading}>
                        <CircularProgress size={20} />
                      </div>
                    )}
                    {!isFetchingServices &&
                      serviceSearchDebounced.length < MIN_SEARCH_LENGTH && (
                        <div className={styles.empty}>
                          {t('salesCrm.calculation.addPosition.search.hint')}
                        </div>
                      )}
                    {!isFetchingServices &&
                      serviceSearchDebounced.length >= MIN_SEARCH_LENGTH &&
                      serviceItems.length === 0 && (
                        <div className={styles.empty}>
                          {t('salesCrm.calculation.addPosition.search.noResults')}
                        </div>
                      )}
                    {!isFetchingServices &&
                      serviceItems.map((item) => {
                        const isSelected = values.serviceId === item.id;
                        return (
                          <button
                            type="button"
                            key={item.id}
                            className={`${styles.resultItem} ${isSelected ? styles.resultItemSelected : ''}`}
                            onClick={() => handleSelectService(item, setValues, values)}>
                            <div className={styles.resultName}>{item.name}</div>
                            {item.taxPercent != null && (
                              <div className={styles.resultMeta}>
                                {t('salesCrm.calculation.addPosition.field.taxPercent')}:{' '}
                                {item.taxPercent}%
                              </div>
                            )}
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}

              {showFormFields && (
              <>
              <TextField
                name="name"
                label={t('salesCrm.calculation.addPosition.field.name')}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name ? String(errors.name) : ''}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />

              <div className={styles.priceTypeGroup}>
                <div className={styles.priceTypeLabel}>
                  {t('salesCrm.calculation.addPosition.field.priceType')}
                  <Tooltip
                    title={
                      <>
                        <div>
                          <strong>{t('salesCrm.calculation.priceType.total')}</strong>:{' '}
                          {t('salesCrm.calculation.priceType.totalHint')}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          <strong>{t('salesCrm.calculation.priceType.unit')}</strong>:{' '}
                          {t('salesCrm.calculation.priceType.unitHint')}
                        </div>
                      </>
                    }
                    arrow
                    placement="top">
                    <InfoOutlinedIcon
                      fontSize="small"
                      className={styles.priceTypeInfoIcon}
                    />
                  </Tooltip>
                </div>
                <RadioGroup
                  row
                  name="priceType"
                  value={values.priceType}
                  onChange={(_, value) => {
                    setFieldValue('priceType', value as CalculationPositionPriceType);
                  }}>
                  <FormControlLabel
                    value="TOTAL"
                    control={<Radio size="small" />}
                    label={t('salesCrm.calculation.priceType.total')}
                  />
                  <FormControlLabel
                    value="UNIT"
                    control={<Radio size="small" />}
                    label={t('salesCrm.calculation.priceType.unit')}
                  />
                </RadioGroup>
              </div>

              <div className={styles.priceRow}>
                <TextField
                  name="priceNet"
                  label={t('salesCrm.calculation.addPosition.field.priceNet')}
                  type="number"
                  value={Number.isNaN(values.priceNet) ? '' : values.priceNet}
                  onChange={(e) => {
                    const next = parseFloat(e.target.value);
                    setFieldValue('priceNet', next);
                    setFieldValue('priceGross', computeGross(next, values.taxPercent));
                  }}
                  onBlur={handleBlur}
                  onKeyDown={blockNonNumericKeys}
                  error={Boolean(touched.priceNet && errors.priceNet)}
                  helperText={touched.priceNet && errors.priceNet ? String(errors.priceNet) : ''}
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        title={t('salesCrm.calculation.addPosition.field.priceNetHint')}
                        arrow
                        placement="top">
                        <InfoOutlinedIcon
                          fontSize="small"
                          className={styles.priceTypeInfoIcon}
                        />
                      </Tooltip>
                    )
                  }}
                />
                <TextField
                  name="priceGross"
                  label={t('salesCrm.calculation.addPosition.field.priceGross')}
                  type="number"
                  value={Number.isNaN(values.priceGross) ? '' : values.priceGross}
                  onChange={(e) => {
                    const next = parseFloat(e.target.value);
                    setFieldValue('priceGross', next);
                    setFieldValue('priceNet', computeNet(next, values.taxPercent));
                  }}
                  onBlur={handleBlur}
                  onKeyDown={blockNonNumericKeys}
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        title={t('salesCrm.calculation.addPosition.field.priceGrossHint')}
                        arrow
                        placement="top">
                        <InfoOutlinedIcon
                          fontSize="small"
                          className={styles.priceTypeInfoIcon}
                        />
                      </Tooltip>
                    )
                  }}
                />
                <TextField
                  name="taxPercent"
                  label={t('salesCrm.calculation.addPosition.field.taxPercent')}
                  type="number"
                  value={Number.isNaN(values.taxPercent) ? '' : values.taxPercent}
                  onChange={(e) => {
                    const next = parseFloat(e.target.value);
                    setFieldValue('taxPercent', next);
                    // Zmiana VAT przelicza brutto z netto (netto traktujemy jako "zrodlo prawdy").
                    setFieldValue('priceGross', computeGross(values.priceNet, next));
                  }}
                  onBlur={handleBlur}
                  onKeyDown={blockNonNumericKeys}
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        title={t('salesCrm.calculation.addPosition.field.taxPercentHint')}
                        arrow
                        placement="top">
                        <InfoOutlinedIcon
                          fontSize="small"
                          className={styles.priceTypeInfoIcon}
                        />
                      </Tooltip>
                    )
                  }}
                />
              </div>

              <div className={styles.priceRow}>
                <TextField
                  name="costNet"
                  label={t('salesCrm.calculation.addPosition.field.costNet')}
                  helperText={t('salesCrm.calculation.addPosition.field.costNetHelp')}
                  type="number"
                  value={values.costNet === null || Number.isNaN(values.costNet) ? '' : values.costNet}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === '') {
                      setFieldValue('costNet', null);
                      return;
                    }
                    const next = parseFloat(raw);
                    setFieldValue('costNet', Number.isNaN(next) ? null : next);
                  }}
                  onBlur={handleBlur}
                  onKeyDown={blockNonNumericKeys}
                  size="small"
                  inputProps={{ min: 0, step: 0.01 }}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <Tooltip
                        title={t('salesCrm.calculation.addPosition.field.costNetHint')}
                        arrow
                        placement="top">
                        <InfoOutlinedIcon
                          fontSize="small"
                          className={styles.priceTypeInfoIcon}
                        />
                      </Tooltip>
                    )
                  }}
                />
              </div>
              </>
              )}

              {errorMessage && <div className={styles.error}>{errorMessage}</div>}

              <div className={styles.actions}>
                <Button onClick={onClose} variant="text" disabled={isSaving}>
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={() => submitForm()}
                  variant="contained"
                  disabled={isSaving || !showFormFields}
                  className={styles.primaryButton}>
                  {isSaving ? (
                    <CircularProgress size={18} />
                  ) : (
                    t('salesCrm.calculation.addPosition.save')
                  )}
                </Button>
              </div>
            </div>
          );
        }}
      </Formik>
    </WiseModal>
  );
};

export default AddCalculationPositionModal;
